// What Sooquna shows across the admin (specs 31): the support-session strip,
// maintenance notice and announcements (banner placement; the feed ones go
// to the home page's news card).
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Headset, Megaphone, X } from "lucide-react";
import { api, storePath } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useStore } from "../lib/store";
import { Banner, Card, cx } from "./ui";

export type Announcement = {
  id: string; title: string; body: string; link_url: string; link_label: string; tone: "info" | "success" | "warning" | "critical";
  placement: "banner" | "feed"; dismissible: boolean; starts_at: string; ends_at: string | null;
};
type Feed = { data: Announcement[]; maintenance: { admin: boolean; storefront: boolean; message: string } };

function useAnnouncements() {
  const { id } = useStore();
  return useQuery({ queryKey: ["store", id, "/announcements"], enabled: !!id, staleTime: 60_000, refetchInterval: 5 * 60_000,
    queryFn: () => api<Feed>(storePath(id!, "/announcements")) });
}

function useDismiss() {
  const { id } = useStore();
  const qc = useQueryClient();
  const key = ["store", id, "/announcements"];
  return useMutation({
    mutationFn: (a: Announcement) => api(storePath(id!, `/announcements/${a.id}/dismiss`), { method: "POST" }),
    onMutate: (a) => qc.setQueryData<Feed>(key, (old) => old && { ...old, data: old.data.filter((x) => x.id !== a.id) }),
    onError: () => void qc.invalidateQueries({ queryKey: key }),
  });
}

// SupportStrip: impossible to miss while Sooquna support acts in the store.
export function SupportStrip() {
  const { t } = useI18n();
  const { summary } = useStore();
  const sup = summary.data?.me.support;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!sup) return;
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [sup]);
  if (!sup) return null;
  const minutes = Math.max(0, Math.ceil((new Date(sup.expires_at).getTime() - now) / 60_000));
  return (
    <div role="status" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-ink px-4 py-2 text-center text-sm text-white">
      <Headset className="size-4 shrink-0" />
      <span className="font-medium">{t("support.banner", { operator: sup.operator })}</span>
      <span className="rounded-full bg-white/15 px-2 py-0.5 text-xs">{t(sup.mode === "write" ? "support.mode_write" : "support.mode_read")}</span>
      <span className="text-white/70">{t("support.ends_in", { minutes })}</span>
    </div>
  );
}

export function PlatformBanners() {
  const { t } = useI18n();
  const feed = useAnnouncements();
  const dismiss = useDismiss();
  const banners = (feed.data?.data ?? []).filter((a) => a.placement === "banner");
  const maint = feed.data?.maintenance;
  return (
    <>
      {(maint?.admin || banners.length > 0) && (
        <div className="space-y-2 px-4 pt-4 sm:px-8">
          {maint?.admin && <Banner tone="warning">{maint.message || t("platform.maintenance")}</Banner>}
          {banners.map((a) => (
            <Banner key={a.id} tone={a.tone === "success" ? "success" : a.tone}
              action={<span className="flex items-center gap-1">
                {a.link_url && <a href={a.link_url} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-sm font-medium text-ink hover:bg-white/60">
                  {a.link_label || t("platform.learn_more")}<ExternalLink className="size-3.5" /></a>}
                {a.dismissible && <button type="button" aria-label={t("platform.dismiss")} onClick={() => dismiss.mutate(a)}
                  className="flex size-8 items-center justify-center rounded-md text-ink-muted hover:bg-white/60 hover:text-ink"><X className="size-4" /></button>}
              </span>}>
              <span className="font-medium">{a.title}</span>{a.body && <span className="block text-[13px] text-ink-muted">{a.body}</span>}
            </Banner>
          ))}
        </div>
      )}
    </>
  );
}

// NewsCard: feed announcements on the home page.
export function NewsCard() {
  const { t, date } = useI18n();
  const feed = useAnnouncements();
  const dismiss = useDismiss();
  const items = (feed.data?.data ?? []).filter((a) => a.placement === "feed");
  if (items.length === 0) return null;
  return (
    <Card title={<span className="inline-flex items-center gap-2"><Megaphone className="size-4 text-brand" />{t("platform.news")}</span>}>
      <ul className="-my-3 divide-y divide-line">
        {items.map((a) => (
          <li key={a.id} className="flex items-start gap-3 py-3">
            <span aria-hidden className={cx("mt-1.5 size-2 shrink-0 rounded-full", a.tone === "critical" ? "bg-danger" : a.tone === "warning" ? "bg-warning" : a.tone === "success" ? "bg-success" : "bg-info")} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">{a.title}</p>
              {a.body && <p className="mt-0.5 text-[13px] text-ink-muted">{a.body}</p>}
              <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-subtle">
                <span>{date(a.starts_at)}</span>
                {a.link_url && <a href={a.link_url} target="_blank" rel="noreferrer" className="text-brand hover:underline">{a.link_label || t("platform.learn_more")}</a>}
              </p>
            </div>
            {a.dismissible && <button type="button" aria-label={t("platform.dismiss")} onClick={() => dismiss.mutate(a)}
              className="flex size-7 items-center justify-center rounded-md text-ink-muted hover:bg-subtle hover:text-ink"><X className="size-4" /></button>}
          </li>
        ))}
      </ul>
    </Card>
  );
}
